// src/pages/playlists/PlaylistLayer.jsx
// Modal / bottom-sheet shell from the handoff, rendered in a portal. Adds what the
// prototype skipped: focus trap, focus restore and body scroll lock. Clicks are
// stopped at the layer so they never reach the archive row behind the portal
// (React bubbles portal events through the component tree).
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { PlsIc } from "./playlistIcons";

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function PlsLayer({ wide = false, label, onClose, children }) {
  const cardRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const previous = document.activeElement;
    const card = cardRef.current;
    if (card && !card.contains(document.activeElement)) card.querySelector(FOCUSABLE)?.focus();

    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        closeRef.current();
        return;
      }
      if (e.key !== "Tab" || !card) return;
      const els = [...card.querySelectorAll(FOCUSABLE)];
      if (!els.length) return;
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
      if (previous && typeof previous.focus === "function") previous.focus();
    };
  }, []);

  return createPortal(
    <div className="pls-layer pls-vars" role="dialog" aria-modal="true" aria-label={label} onClick={(e) => e.stopPropagation()}>
      <div className="pls-scrim" onClick={() => closeRef.current()} />
      <div ref={cardRef} className={`pls-card ${wide ? "wide" : ""}`}>
        <span className="pls-grab" />
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function PlsHead({ title, onClose }) {
  return (
    <div className="pls-head">
      <h3>{title}</h3>
      <button type="button" className="pls-x" onClick={onClose} aria-label="Close">{PlsIc.x(17)}</button>
    </div>
  );
}
